import {Column, ColumnApi, GridApi} from 'ag-grid-community';
import {getApi} from '../apis/backendApis';



export type ExportFormat = 'csv' | 'xlsx';


const CHUNK_SIZE = 2000;

const CSV_DELIMITER = ';';
const CSV_BOM = '﻿';

export interface ExportColumn {
    header: string;
    format: (row: any) => string;
}

const triggerDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};


export const fetchAllRows = async (
    rota: string,
    filter?: string,
    sort?: string,
    onProgress?: (carregadas: number) => void
): Promise<any[]> => {
    const api = getApi();
    const rows: any[] = [];
    let start = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const response = await api.get(rota, {
            params: {filter, sort, export: 1, limit: CHUNK_SIZE, start}
        });
        const bloco: any[] = response.data?.rows ?? [];
        rows.push(...bloco);
        if (onProgress) {
            onProgress(rows.length);
        }
        if (bloco.length < CHUNK_SIZE) {
            return rows;
        }
        start += CHUNK_SIZE;
    }
};


export const buildColumns = (
    columns: Column[],
    gridApi?: GridApi,
    columnApi?: ColumnApi
): ExportColumn[] => {
    const exportColumns: ExportColumn[] = [];

    columns.forEach((column) => {
        const colDef: any = column.getColDef() || {};
        const field: string | undefined = colDef.field;
        const valueGetter = colDef.valueGetter;
        const valueFormatter = colDef.valueFormatter;

        // Coluna sem origem de dado (só renderização) não tem o que exportar.
        if (!field && typeof valueGetter !== 'function') {
            return;
        }

        exportColumns.push({
            header: colDef.headerName || field || column.getColId(),
            format: (row: any) => {
                // Os campos aninhados chegam do backend como chave literal
                // ('pessoa/nome'), então row[field] resolve direto.
                const params: any = {
                    value: field ? row[field] : undefined,
                    data: row,
                    node: null,
                    colDef,
                    column,
                    columnApi,
                    api: gridApi,
                    context: null,
                    getValue: (campo: string) => row[campo]
                };

                if (typeof valueGetter === 'function') {
                    params.value = valueGetter(params);
                }

                const value = typeof valueFormatter === 'function'
                    ? valueFormatter(params)
                    : params.value;

                if (value === null || value === undefined) {
                    return '';
                }
                return String(value);
            }
        });
    });

    return exportColumns;
};

export const buildMatrix = (columns: ExportColumn[], rows: any[]): string[][] => {
    const matrix: string[][] = [columns.map((column) => column.header)];
    rows.forEach((row) => {
        matrix.push(columns.map((column) => column.format(row)));
    });
    return matrix;
};

const escapeCsv = (value: string): string => {
    if (value.indexOf(CSV_DELIMITER) >= 0 || value.indexOf('"') >= 0 || /[\r\n]/.test(value)) {
        return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
};

export const downloadCsv = (matrix: string[][], filename: string) => {
    const conteudo = matrix
        .map((line) => line.map(escapeCsv).join(CSV_DELIMITER))
        .join('\r\n');

    triggerDownload(new Blob([CSV_BOM + conteudo], {type: 'text/csv;charset=utf-8'}), filename);
};

export const downloadXlsx = async (matrix: string[][], filename: string) => {
    // Import dinâmico: a exceljs só entra no bundle de quem exporta Excel.
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Dados');

    matrix.forEach((line) => worksheet.addRow(line));
    if (matrix.length > 0) {
        worksheet.getRow(1).font = {bold: true};
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    triggerDownload(new Blob([buffer], {type}), filename);
};


export const buildFilename = (rota: string, format: ExportFormat, title?: string): string => {
    let base = title && title.length > 0
        ? title
        : rota.split('/').filter((parte) => parte !== '' && parte !== 'list')[0];

    base = (base || 'consulta')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase();

    const agora = new Date();
    const pad = (valor: number) => String(valor).padStart(2, '0');
    const carimbo = `${agora.getFullYear()}${pad(agora.getMonth() + 1)}${pad(agora.getDate())}`
        + `_${pad(agora.getHours())}${pad(agora.getMinutes())}${pad(agora.getSeconds())}`;

    return `${base || 'consulta'}_${carimbo}.${format}`;
};
